import { coreApi } from '$api'
import { LeaveRequestStatus } from '$constants'
import {
  FileParameter,
  ILeaveRequest,
  ILeaveRequestFile,
  ILeaveType,
  LeaveRequestFile,
  LeaveRequestUpdateRequest,
  transformLeaveRequestOnGet,
  transformLeaveRequestOnSave,
} from '$models'
import { base64ToBlob, singletonQueryClient } from '$utils'

enum QueryKeys {
  LeaveFile = 'leave-file',
  LeaveRequests = 'leave-requests',
  LeaveRequest = 'leave-request',
}

/**
 * Get Leave Request list
 * @param staffMemberIds optional staff member ids filter
 * @param from optional from date filter to get leave requests after this date (e.g. "2025-02-20")
 * @param to optional to date filter to get leave requests before this date (e.g. "2025-02-20")
 * @param statuses optional filter to only fetch leave requests of a certain status
 * @param requestedFrom optional from date filter to get leave requested at or after this date
 * @param requestedTo optional to date filter to get leave requested before this date
 * @returns Promise<ILeaveRequest[]>
 */
export const getAll = async (
  staffMemberIds?: string[],
  from?: string,
  to?: string,
  statuses?: LeaveRequestStatus[],
  requestedFrom?: string,
  requestedTo?: string
): Promise<ILeaveRequest[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.LeaveRequests,
      staffMemberIds,
      from,
      to,
      statuses,
      requestedFrom,
      requestedTo,
    ],
    queryFn: async () => {
      const result = await coreApi.getLeaveRequests(
        staffMemberIds,
        from,
        to,
        statuses,
        requestedFrom,
        requestedTo
      )
      return result.map(transformLeaveRequestOnGet)
    },
  })
}

/**
 * Get a Leave Request by Id
 * @param id the Id of the Leave Request
 * @returns the Leave Request with the given Id
 */
export const getById = async (id: string): Promise<ILeaveRequest> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.LeaveRequest, id],
    queryFn: async () => {
      const result = await coreApi.getLeaveRequestById(id)
      return transformLeaveRequestOnGet(result)
    },
  })
}

/**
 * Add a new Leave Request
 * @param leaveRequest The Leave Request to create
 * @returns Promise<ILeaveRequest>
 */
export const add = async (
  leaveRequest: ILeaveRequest,
  rawFiles?: File[]
): Promise<ILeaveRequest> => {
  const leaveFiles: ILeaveRequestFile[] = []
  if (rawFiles?.length) {
    // Process each file individually or as a batch depending on API
    for (const file of rawFiles) {
      // Each individual file should be passed as a FileParameter
      const fileParameter: FileParameter = {
        data: file, // Use the file directly, not wrapped in FormData
        fileName: `leave-request-${file.name}`,
      }

      // Upload each file
      const fileUploadResult = await coreApi.uploadLeaveRequestFile(
        fileParameter
      )

      leaveFiles.push({
        id: undefined,
        fileId: fileUploadResult.id,
        leaveRequestId: undefined,
      })
    }
  }

  leaveRequest.files = leaveFiles as LeaveRequestFile[]

  const transformedLeaveRequest = transformLeaveRequestOnSave(leaveRequest)

  const result = await coreApi.createLeaveRequest(transformedLeaveRequest)

  return transformLeaveRequestOnGet(result)
}

/**
 * Send an existing leave request by Id
 * @param id Id of the leave request to send
 * @returns Promise<ILeaveRequest>
 */
export const sendRequest = async (id: string): Promise<ILeaveRequest> => {
  const result = await coreApi.sendLeaveApprovalRequest(id)
  return transformLeaveRequestOnGet(result)
}

/**
 * Update a leave request status.
 * @param id Id of the leave request
 * @param status Status to update
 * @returns Promise<ILeaveRequest>
 */
export const updateStatus = async (
  id: string,
  status: LeaveRequestStatus
): Promise<ILeaveRequest> => {
  const payload = {
    status: status,
  } as LeaveRequestUpdateRequest
  const result = await coreApi.updateLeaveRequestStatus(id, payload)
  return transformLeaveRequestOnGet(result)
}

export const getLeaveType = async (id: string): Promise<ILeaveType> => {
  const result = await coreApi.getLeaveTypeById(id)
  return result
}

export const getLeaveRequestFiles = async (
  fileIds: string
): Promise<File[]> => {
  const response = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.LeaveFile, fileIds],
    queryFn: async () => {
      return await coreApi.downloadLeaveRequestFiles(fileIds)
    },
  })

  return response.map((file, idx) => {
    const blob = base64ToBlob(file.data, file.contentType)
    return new File([blob], file?.id ?? `leave-request-file-${idx}`, {
      type: file.contentType,
    })
  })
}
